/**
 * Conversation Scroll (dev only)
 *
 * Mounts the REAL {@link ConversationThread} (AnchoredViewport + composer)
 * with a statically seeded, overflowing conversation so the load/reload
 * scroll behaviour can be exercised without logging in or running an agent.
 *
 * What it's for:
 *  - **Initial load / reload** — on mount the viewport must land at the bottom
 *    (latest message), even though heavy content (code blocks, tables, long
 *    lists) finishes laying out a few frames after the first paint. Use the
 *    **Remount** button to re-trigger the initial-load path without a browser
 *    refresh, or just hit Cmd+R — both must settle at the bottom.
 *  - **Read-back is not yanked** — after load, scroll up to read history; an
 *    unrelated mutation must NOT snap you back to the bottom.
 *  - **New-turn top-anchor** — type a message and send; the canned streaming
 *    adapter pins your new user message to the top while the reply streams in.
 *  - **Full CoT cycle** — the "Full CoT cycle" scenario auto-runs a recorded
 *    `agent.run.*` fixture (reasoning → tools → final answer) through the real
 *    pipeline, so the runStart pin → CoT timeline growth → collapse-to-answer
 *    transition is exercised end-to-end. Reuses the CoT-playback fixtures.
 *
 * Visit `/dev/conversation-scroll` in dev mode. Extend `SCENARIOS` below.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  useLocalRuntime,
  type ChatModelAdapter,
  type ChatModelRunResult,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import '@assistant-ui/react-ui/styles/index.css';
import '@assistant-ui/react-ui/styles/markdown.css';
import { PlatformEventContext } from '@/contexts/PlatformEventContext';
import type { SubscribeFn } from '@/contexts/PlatformEventContext';
import { CanvasProvider } from '@/contexts/CanvasContext';
import { ThoughtsProvider } from '@/contexts/ThoughtsContext';
import { ConversationThread } from '@/components/assistant-ui/conversation-thread';
import {
  createRunStreamState,
  applyRunEvent,
  buildRunContent,
  type RunStreamState,
} from '@/utils/run-stream-state';
import { runStateStore } from '@/hooks/useAsyncInvokeAdapter';
import { FIXTURES, type PlaybackFixture } from './cot-playback-fixtures';
import type { Canvas, Surface } from '@/types/canvases';
import type { PlatformEvent, RunFinalAnswerDeltaData, EventFilter } from '@/types/events';

const CONV_ID = 'conv-scroll';
const CVS_ID = 'cvs-scroll';

const fakeCanvas: Canvas = {
  canvasId: CVS_ID,
  conversationId: CONV_ID,
  title: 'Scroll Canvas',
  isDefault: true,
  layout: 'stack',
  createdAt: new Date().toISOString(),
};
const fakeSurfaces: Surface[] = [];

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Canned streaming adapter so that *sending* a message exercises the
 * `thread.runStart` top-anchor path (the new user message pins to the top and
 * the reply expands below it). Mirrors the production async generator shape:
 * an initial reasoning yield, growing text, then a terminal `complete`.
 */
// runId of the most recently started run (streamed reply OR CoT turn), so the
// page's "+ Surface ref" button can dispatch a late `agent.run.surface_update`
// that SurfaceLinkBadges matches — reproducing the late-surface-ref scroll.
let lastRunId: string | null = null;
let surfaceSeq = 0;

let replyCounter = 0;
const streamingAdapter: ChatModelAdapter = {
  run: async function* (): AsyncGenerator<ChatModelRunResult> {
    const runId = `run-scroll-reply-${replyCounter++}`;
    lastRunId = runId; // let "+ Surface ref" target this streamed turn
    // Stamp the run's start in the store so the single-turn "Thought for Xs"
    // fallback header reads elapsed-since-run-start (matches production, where
    // the real adapter populates the store on every yield).
    runStateStore.updateProgress(runId, {});
    yield {
      content: [{ type: 'reasoning' as const, text: 'Looking into that…' }],
      metadata: { custom: { runId } },
    } as ChatModelRunResult;

    // Simulate a "thinking" gap before the first answer token so the
    // "Thought for Xs" header freezes to a visible duration.
    await delay(1600);

    const paragraphs = [
      'Here is a streamed reply so you can watch the **top-anchor** behaviour: your message above should stay pinned to the top while this answer grows beneath it.',
      '\n\n- It expands line by line\n- The viewport must not bounce to the bottom and back\n- A short reply leaves your message near the top; a long one fills the panel',
      '\n\n```ts\n// a fenced block lands a frame late — the anchor must hold\nexport function ping(label: string) {\n  return `pong:${label}`;\n}\n```',
      '\n\nThat’s the end of the canned response.',
    ];
    let text = '';
    for (const p of paragraphs) {
      text += p;
      await delay(220);
      yield {
        content: [{ type: 'text' as const, text }],
        metadata: { custom: { runId } },
      } as ChatModelRunResult;
    }

    yield {
      content: [{ type: 'text' as const, text }],
      status: { type: 'complete' as const, reason: 'stop' as const },
      metadata: { custom: { runId, runDurationMs: 1280 } },
    } as ChatModelRunResult;
  },
};

// ─── CoT playback (full live run from a recorded fixture) ────────────────────

/**
 * Self-driving variant of CotPlaybackPage's adapter: on `run()` it walks the
 * fixture's `agent.run.*` events itself (a `stepMs` pause between each) through
 * the SAME pipeline — `applyRunEvent` → `runStateStore` → `buildRunContent` —
 * so the CoT renders identically, but without the manual stepper. The result
 * is a full reasoning → tools → final-answer cycle that drives the real
 * AnchoredViewport: runStart pins the user message, the timeline grows below
 * it, then collapses to "Thought for Xs" + the answer.
 *
 * `runId` is synthetic and used for BOTH the message's `metadata.custom.runId`
 * and the `runStateStore` key (the renderer subscribes by it) — exactly as the
 * playback page does. `onEvent` fans each event out to platform subscribers so
 * any delegated task cards in the fixture stay in sync.
 */
function createCotAdapter(
  events: PlatformEvent[],
  runId: string,
  stepMs: number,
  onEvent: (e: PlatformEvent) => void,
): ChatModelAdapter {
  return {
    run: async function* (): AsyncGenerator<ChatModelRunResult> {
      yield {
        content: [{ type: 'reasoning' as const, text: '' }],
        metadata: { custom: { runId } },
      } as ChatModelRunResult;

      const state: RunStreamState = createRunStreamState();
      for (const event of events) {
        await delay(stepMs);
        applyRunEvent(state, event);
        runStateStore.updateLive(runId, state.iterations);
        onEvent(event);

        if (state.hasFinalAnswer) runStateStore.updateProgress(runId, { hasFinalAnswer: true });
        if (event.eventType === 'agent.run.final_answer_delta') {
          const data = event.data as unknown as RunFinalAnswerDeltaData;
          const iter = state.iterations.get(data.iteration);
          if (iter?.finalAnswerText) runStateStore.updateProgress(runId, { hasTextContent: true });
        }

        if (state.terminal) {
          const t = state.terminal;
          runStateStore.updateProgress(runId, { isRunComplete: true });
          if (t.durationMs != null) runStateStore.updateDuration(runId, t.durationMs);
          yield {
            content: buildRunContent(state),
            metadata: { custom: { runId } },
          } as ChatModelRunResult;
          yield {
            content: buildRunContent(state),
            status: { type: 'complete' as const, reason: 'stop' as const },
            metadata: { custom: { ...(t.durationMs != null && { runDurationMs: t.durationMs }), runId } },
          } as ChatModelRunResult;
          return;
        }

        yield {
          content: buildRunContent(state),
          metadata: { custom: { runId } },
        } as ChatModelRunResult;
      }

      // Fixture had no terminal event — finish gracefully.
      yield {
        content: buildRunContent(state),
        status: { type: 'complete' as const, reason: 'stop' as const },
        metadata: { custom: { runId } },
      } as ChatModelRunResult;
    },
  };
}

/** Platform-event fan-out (manual dispatch instead of WS frames) so delegated
 *  task cards subscribing via `usePlatformEvents` see the same stream the CoT
 *  adapter plays. Copied from CotPlaybackPage's `usePlaybackPlatformEvents`. */
function usePlaybackPlatformEvents() {
  type Sub = { filters: EventFilter[]; handler: (event: PlatformEvent) => void };
  const subsRef = useRef<Set<Sub>>(new Set());

  const subscribe: SubscribeFn = useCallback((filters, handler) => {
    const sub: Sub = { filters, handler };
    subsRef.current.add(sub);
    return () => { subsRef.current.delete(sub); };
  }, []);

  const dispatch = useCallback((event: PlatformEvent) => {
    for (const sub of subsRef.current) {
      if (eventMatchesFilters(event, sub.filters)) {
        try { sub.handler(event); } catch (e) { console.error('[dev scroll] handler threw', e); }
      }
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      subscribe,
      isConnected: true,
      diagnostics: { connectedSince: null, disconnectCount: 0, lastDisconnectReason: null, lastDisconnectTime: null, reconnectAttempts: 0 },
    }),
    [subscribe],
  );

  return { contextValue, dispatch };
}

function eventMatchesFilters(event: PlatformEvent, filters: EventFilter[]): boolean {
  if (filters.length === 0) return true;
  return filters.some((f) => filterMatches(event, f));
}

function filterMatches(event: PlatformEvent, f: EventFilter): boolean {
  if (f.eventType) {
    if (f.eventType.endsWith('.*')) {
      if (!event.eventType.startsWith(f.eventType.slice(0, -1))) return false;
    } else if (f.eventType !== event.eventType) {
      return false;
    }
  }
  if (f.runId) {
    const data = event.data as Record<string, unknown> | undefined;
    const matches = event.subjectId === f.runId || (data?.runId as string | undefined) === f.runId;
    if (!matches) return false;
  }
  if (f.subjectId && event.subjectId !== f.subjectId) return false;
  if (f.source && event.source !== f.source) return false;
  return true;
}

// ─── Seed content ────────────────────────────────────────────────────────────

const CODE_BLOCK =
  '```ts\n' +
  'export async function processBatch(items: Item[], opts: BatchOpts) {\n' +
  '  const results: Result[] = [];\n' +
  '  for (const item of items) {\n' +
  '    const r = await handle(item, opts);\n' +
  '    if (r.ok) results.push(r.value);\n' +
  '    else logger.warn("skipped", { id: item.id, reason: r.error });\n' +
  '  }\n' +
  '  return results;\n' +
  '}\n' +
  '```';

const TABLE =
  '| Metric | Before | After |\n' +
  '| --- | --- | --- |\n' +
  '| Duration | 12.4s | 7.1s |\n' +
  '| Tool calls | 18 | 11 |\n' +
  '| Output tokens | 2,140 | 2,690 |';

const RICH_ANSWER =
  'Done — here is a fuller write-up so the message has real height:\n\n' +
  '### Summary\n\n' +
  'I went through the pipeline end-to-end and applied the changes you asked for. ' +
  'A few notes on what moved and why.\n\n' +
  '1. **Batching** — grouped the per-item calls so we issue fewer round-trips.\n' +
  '2. **Logging** — skipped items are now warned, not dropped silently.\n' +
  '3. **Types** — tightened `BatchOpts` so a bad config fails at compile time.\n\n' +
  CODE_BLOCK +
  '\n\nAnd the before/after comparison:\n\n' +
  TABLE +
  '\n\nWant me to apply the same treatment to the export path?';

const SHORT_ANSWER = 'Yep — that’s done. Anything else?';

/** Build an N-turn conversation; the final assistant turn uses `finalAnswer`. */
function buildConversation(turns: number, finalAnswer: string): ThreadMessageLike[] {
  const msgs: ThreadMessageLike[] = [];
  for (let i = 0; i < turns; i++) {
    const isLast = i === turns - 1;
    msgs.push({ role: 'user', content: `Turn ${i + 1}: ${USER_PROMPTS[i % USER_PROMPTS.length]}` });
    msgs.push({
      role: 'assistant',
      content: [{ type: 'text', text: isLast ? finalAnswer : ASSISTANT_REPLIES[i % ASSISTANT_REPLIES.length] }],
      metadata: { custom: { runId: `run-seed-${i}`, runDurationMs: 900 + i * 130 } },
    });
  }
  return msgs;
}

const USER_PROMPTS = [
  'Walk me through how the batch processor handles failures.',
  'Can you tighten the config types?',
  'Show me a before/after on the run metrics.',
  'What changed in the logging?',
  'Summarise everything you did so far.',
];

const ASSISTANT_REPLIES = [
  'On failure the item is skipped and a warning is logged with its id and reason, so a single bad record never aborts the batch. The rest continue.\n\n' + CODE_BLOCK,
  'Tightened `BatchOpts` — the concurrency and retry fields are now required and validated, so a malformed config is a **compile-time** error rather than a runtime surprise.',
  'Here are the numbers from the last run:\n\n' + TABLE,
  'Skipped items used to vanish silently. They now emit a `logger.warn` with `{ id, reason }`, so they show up in the run trace and can be triaged.',
  '- Batched the per-item calls\n- Added warnings for skipped items\n- Tightened the config types\n- Captured before/after metrics',
];

/** A scenario is either a statically seeded conversation, or a live CoT run
 *  played from a recorded fixture. */
type ScenarioDef =
  | { id: string; title: string; expect: string; kind: 'static'; messages: ThreadMessageLike[] }
  | { id: string; title: string; expect: string; kind: 'cot'; fixture: PlaybackFixture; stepMs: number };

const COT_FIXTURE =
  FIXTURES.find((f) => f.id === 'minimal-3-tool') ?? FIXTURES[0];

const SCENARIOS: ScenarioDef[] = [
  {
    id: 'long-long',
    title: 'Long history · long final answer',
    kind: 'static',
    expect: 'On load/Remount the viewport lands at the very bottom — the full rich final answer (code + table) is visible without scrolling up.',
    messages: buildConversation(7, RICH_ANSWER),
  },
  {
    id: 'long-short',
    title: 'Long history · short final turn',
    kind: 'static',
    expect: 'On load/Remount the viewport lands at the TRUE bottom — the one-line answer sits directly above the composer with no reserved blank screen (the pin reservation only exists while a run’s pin is held).',
    messages: buildConversation(8, SHORT_ANSWER),
  },
  {
    id: 'cot-cycle',
    title: 'Full CoT cycle (live run)',
    kind: 'cot',
    expect: 'On mount/Remount a live run auto-starts: the user message pins to the TOP, the CoT timeline streams (reasoning → 3 tools → final answer) below it, then collapses to "Thought for Xs" + the answer. The viewport must not bounce to the bottom and back during streaming.',
    fixture: COT_FIXTURE,
    stepMs: 350,
  },
  {
    id: 'short',
    title: 'Short conversation (no overflow)',
    kind: 'static',
    expect: 'Content is shorter than the panel — no scrollbar, nothing snaps. Sanity check that the spacer/anchor logic is inert when there is nothing to scroll.',
    messages: buildConversation(2, SHORT_ANSWER),
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────

const PANEL_HEIGHTS = [480, 640, 800] as const;
const PANEL_WIDTHS = [720, 560, 420] as const;

/** Static scenario: owns the runtime so a fresh mount reseeds
 *  `initialMessages` — `useLocalRuntime` only reads them on first mount. */
function StaticScrollThread({ messages }: { messages: ThreadMessageLike[] }) {
  const runtime = useLocalRuntime(streamingAdapter, { initialMessages: messages });
  return <ConversationThread runtime={runtime} />;
}

// One kick per synthetic runId. Module-level so React Strict Mode's
// double-invoked mount effect (records on the first pass, skips on the second)
// doesn't append the user message twice. Mirrors CotPlaybackPage.
const cotKicked = new Set<string>();
let cotRunSeq = 0;

/** CoT scenario: empty thread + a one-shot "kick" that appends the user
 *  message, which fires the playback adapter's `run()` → the full live cycle. */
function CotScrollThread({
  fixture,
  stepMs,
  dispatch,
}: {
  fixture: PlaybackFixture;
  stepMs: number;
  dispatch: (e: PlatformEvent) => void;
}) {
  const runId = useMemo(() => `run-cot-scroll-${cotRunSeq++}`, []);
  const adapter = useMemo(
    () => createCotAdapter(fixture.events, runId, stepMs, dispatch),
    [fixture, runId, stepMs, dispatch],
  );
  // NOTE: do NOT pass `{ initialMessages }` here. ANY initialMessages array
  // (even non-empty) makes useLocalRuntime drop the appended run's streamed
  // parts (the message renders blank); omit the option entirely — as the
  // working CotPlaybackPage does.
  const runtime = useLocalRuntime(adapter);

  useEffect(() => {
    lastRunId = runId;
    if (cotKicked.has(runId)) return;
    cotKicked.add(runId);
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text: 'Run the demo task — 3 tools, then summarise.' }],
    });
  }, [runtime, runId]);

  return <ConversationThread runtime={runtime} />;
}

function ScrollThread({
  scenario,
  dispatch,
}: {
  scenario: ScenarioDef;
  dispatch: (e: PlatformEvent) => void;
}) {
  if (scenario.kind === 'cot') {
    return <CotScrollThread fixture={scenario.fixture} stepMs={scenario.stepMs} dispatch={dispatch} />;
  }
  return <StaticScrollThread messages={scenario.messages} />;
}

export default function ConversationScrollPage() {
  const [scenarioId, setScenarioId] = useState(SCENARIOS[0].id);
  const [height, setHeight] = useState<number>(800);
  // Width is applied IN PLACE (no remount) so changing it resizes the live
  // panel — exercising AnchoredViewport's ResizeObserver exactly like dragging
  // the real OperatorChatPanel divider. `null` = full width.
  const [width, setWidth] = useState<number | null>(null);
  // `mounted` gates the thread so every remount is a clean unmount → (gap) →
  // mount rather than an atomic keyed swap. The gap lets the composer's
  // mention-popover portals fully tear down before the new instance mounts —
  // an atomic swap races that teardown and throws a TriggerPopover context
  // error. It also more faithfully simulates a real page reload.
  const [mounted, setMounted] = useState(true);

  const reload = useCallback(() => {
    setMounted(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
  }, []);

  const changeScenario = useCallback(
    (id: string) => { setScenarioId(id); reload(); },
    [reload],
  );
  const changeHeight = useCallback(
    (h: number) => { setHeight(h); reload(); },
    [reload],
  );

  const scenario = useMemo(
    () => SCENARIOS.find((s) => s.id === scenarioId) ?? SCENARIOS[0],
    [scenarioId],
  );

  // Own QueryClient so the composer's mention hooks (useMeAgents,
  // useMentionFileCandidates) resolve to empty without hitting the backend —
  // overrides the global client from main.tsx for this subtree.
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { queryFn: async () => ({ items: [] }), retry: false, staleTime: Infinity, gcTime: Infinity },
        },
      }),
    [],
  );

  // Dispatching platform-event context so the CoT scenario's delegated task
  // cards (if any) stay in sync; inert for static scenarios (no events fired).
  const { contextValue: eventCtx, dispatch } = usePlaybackPlatformEvents();

  // Dispatch a late `agent.run.surface_update` for the last CoT run — its
  // SurfaceLinkBadge appears below that message, reproducing the late-surface-
  // reference scroll behaviour. Run the CoT scenario first, then click this.
  const addSurfaceRef = useCallback(() => {
    if (!lastRunId) return;
    surfaceSeq += 1;
    dispatch({
      eventId: `evt-surf-${surfaceSeq}`,
      eventType: 'agent.run.surface_update',
      source: 'agent-core',
      subjectId: lastRunId,
      data: {
        runId: lastRunId,
        agentId: 'agt-scroll',
        canvasId: CVS_ID,
        surfaceId: `srf-scroll-${surfaceSeq}`,
        title: 'Claude Code Agent — Plan Storage Section',
        version: 1,
        spec: { updates: [] },
        iteration: 0,
        conversationId: CONV_ID,
      },
      metadata: { timestamp: new Date().toISOString() },
      orgId: 'org-test',
    } as unknown as PlatformEvent);
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <header className="mb-6 border-b border-border pb-4">
          <h1 className="text-2xl font-bold">Conversation Scroll</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The real <code className="rounded bg-muted px-1 py-0.5">ConversationThread</code> with a
            seeded, overflowing conversation — or a live CoT run played from a recorded fixture.
            Reload the page (or click <strong>Remount</strong>) and the viewport should land at the
            bottom / latest message; the CoT scenario re-runs the full cycle. Dev only.
          </p>
        </header>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border p-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Scenario:</span>
            <select
              id="scenario-select"
              className="rounded border border-border bg-background px-2 py-1"
              value={scenarioId}
              onChange={(e) => changeScenario(e.target.value)}
            >
              {SCENARIOS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Panel height:</span>
            <select
              id="height-select"
              className="rounded border border-border bg-background px-2 py-1"
              value={height}
              onChange={(e) => changeHeight(Number(e.target.value))}
            >
              {PANEL_HEIGHTS.map((h) => (
                <option key={h} value={h}>
                  {h}px
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Panel width:</span>
            <select
              id="width-select"
              className="rounded border border-border bg-background px-2 py-1"
              value={width ?? 'full'}
              onChange={(e) =>
                setWidth(e.target.value === 'full' ? null : Number(e.target.value))
              }
            >
              <option value="full">Full</option>
              {PANEL_WIDTHS.map((w) => (
                <option key={w} value={w}>
                  {w}px
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="rounded bg-primary px-3 py-1 text-primary-foreground"
            onClick={reload}
            title="Remount the thread (re-runs initial-load scroll-to-bottom, or re-runs the CoT cycle)"
          >
            Remount ⟳
          </button>

          <button
            type="button"
            className="rounded border border-border px-3 py-1"
            onClick={addSurfaceRef}
            title="Dispatch a late surface_update for the last CoT run — its SurfaceLinkBadge appears below the message (repro for the late-surface-ref scroll)"
          >
            + Surface ref
          </button>
        </div>

        <p className="mb-4 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <strong className="text-foreground/80">Expect:</strong> {scenario.expect}
        </p>

        <QueryClientProvider client={queryClient}>
          <PlatformEventContext.Provider value={eventCtx}>
            <CanvasProvider
              conversationId={CONV_ID}
              initialCanvas={{ canvas: fakeCanvas, surfaces: fakeSurfaces }}
            >
              <ThoughtsProvider>
                <div
                  className="overflow-hidden rounded-lg border border-border transition-none"
                  style={{ height, width: width ?? undefined }}
                >
                  {mounted && <ScrollThread scenario={scenario} dispatch={dispatch} />}
                </div>
              </ThoughtsProvider>
            </CanvasProvider>
          </PlatformEventContext.Provider>
        </QueryClientProvider>
      </div>
    </div>
  );
}
